/*
    A class that manages the point cloud loaded into the scene
*/

import * as THREE from 'three';
import { Box3, Intersection, Mesh, Object3D, Points, Raycaster, Scene } from 'three';

import { Cache } from '../../cache/cache-manager';
import { SkyviewOrthographicCamera } from '../../cameras/skyview-orthographic-camera';
import { SkyviewCamera } from '../../cameras/types';
import { PointCloud } from '../../geodata/point-clouds/types';
import { ProjectGeodataManager } from '../../project-geodata-manager';
// @ts-ignore
import state from '../../state';
import { Point3d } from '../../utilities/math-helpers';
import { OctNodeI } from '../../utilities/octTree/octTree';
import { closestToRay } from '../../utilities/pick-near-cursor';
import { getWebWorkerManager } from '../../workers/web-worker-manager';
import { PCLoader } from './point-cloud-loader';

export interface IntersectedPointChunk {
  object: Mesh;
  distance: number;
}

const LODLoadingDepths = [4, 8, 12, 25, 50, 100, 200, 500, 750, 1000];

export class PointCloudManager extends PointCloud {
  private static _instance: PointCloudManager = new PointCloudManager();
  static get instance(): PointCloudManager {
    return this._instance;
  }

  private initialized = false;
  private destroyed = false;
  private pointScene = new Scene();

  private cache: Cache;

  // This is the concurrent mesh download
  // This is to limit the number of scan chunks being downloaded per webworker
  // Large number has least overhead, smaller has better response time per download
  private concurrentSize = 6;

  constructor() {
    super();

    this.cache = new Cache(state.getValue('pointcloudCacheTime') as number);
  }

  /**
   *  Fetches the point cloud configuration.
   */
  loadPointcloud() {
    // Is the geodata loaded?
    if (
      ProjectGeodataManager.instance().isLoading() ||
      !ProjectGeodataManager.instance().hasPointcloudData()
    ) {
      return;
    }

    // Do not re-initialize when done
    if (this.initialized) {
      return;
    }

    getWebWorkerManager().init('pointcloudChunk');

    this.initialized = true;
    this.destroyed = false;
  }

  /**
   *  Activate the point cloud manager so that it start fetches additional chunks
   */
  activate() {
    this.destroyed = false;
  }

  cullLodChunks(camera: SkyviewCamera): void {
    // Not used as it is already handled in loadLodChunks
  }

  getBoundingBox(): THREE.Box3 {
    return new Box3();
  }

  async getMaxLodChunksFromRays(): Promise<Points[]> {
    return Promise.resolve([]);
  }

  async getMaxLodChunksWithinBoundingBox(bbox: THREE.Box3): Promise<Points[]> {
    return Promise.resolve([]);
  }

  getOrigo() {
    return ProjectGeodataManager.instance().getActiveScanData()!.origo;
  }

  /**
   *  Go through the point cloud tree hierarchy to identify and queue the point cloud chunks that are within the camera frustum
   * @param camera
   * @param maxDepth
   */
  async loadLodChunks(camera: SkyviewCamera, maxDepth = state.getValue('LODLevelPointcloudDepth')) {
    const pointCloudGeodata = ProjectGeodataManager.instance().getActivePointcloudGeodata();
    if (!pointCloudGeodata || !pointCloudGeodata.pointCloudStore || !pointCloudGeodata.octtree) {
      return;
    }

    if (!state.getValue('loadPointcloud')) {
      return;
    }

    if (this.pointScene) {
      this.pointScene = new Scene();
    }

    this.cache.tick();

    const octTree = pointCloudGeodata.octtree;
    const treeDepth = octTree.getDepth();

    const LODdistances = LODLoadingDepths.slice(0, treeDepth);
    LODdistances.reverse();

    // The octree is in the point cloud's own coordinates, around the scan's origo, which is at
    // the point cloud's position in the scene, see PointCloudHandle
    const frustum = camera.getFrustum().clone();
    for (const plane of frustum.planes) {
      plane.translate(this.position.clone().negate());
    }
    const maxNodeSize = state.getValue('maxNodeSize') / state.getValue('nodeSizeFactor');

    const position = new THREE.Vector3();
    camera.getTHREECamera().getWorldPosition(position);
    position.x -= this.position.x;
    position.y -= this.position.y;
    // The orthographic camera's height is how far it sees, not where it is
    if (camera instanceof SkyviewOrthographicCamera) {
      position.z = camera.calculatedDistance;
    } else {
      position.z = camera.getTHREECamera().position.z - this.position.z;
    }

    const chunks = octTree.filter(frustum, LODdistances, position, maxDepth as number, maxNodeSize);

    await this.loadPointcloudChunks(
      chunks.reduce((prev: string[], chunk: OctNodeI) => {
        const pointcloud = chunk.data.pointcloud as string;
        if (!pointcloud || prev.length >= this.concurrentSize) {
          return prev;
        }

        if (!this.cache.isQueued(pointcloud)) {
          if (this.cache.isCached(pointcloud)) {
            this.pointScene.add(this.cache.get(pointcloud) as Object3D);
          } else {
            prev.push(pointcloud);
            this.cache.queue(pointcloud);
          }
        }
        return prev;
      }, []),
    );
  }

  getOctreeGeometry() {
    return this.pointScene;
  }

  /**
   *  Returns if the current project has pointcloud data available
   */
  available() {
    return ProjectGeodataManager.instance().hasPointcloudData() && state.getValue('loadPointcloud');
  }

  /**
   *  Loads an array of pointcloud chunks and caches them for later use
   * @param parts
   */
  async loadPointcloudChunks(parts: string[]) {
    const pointCloudStore =
      ProjectGeodataManager.instance().getActivePointcloudGeodata()!.pointCloudStore;
    const pcS3URLs = await Promise.all(
      parts.map(async (part) => {
        return { part, s3URL: await pointCloudStore.fetchPcdUrl(part) };
      }),
    );

    const loadedPCChunks = await getWebWorkerManager().spawnPointcloudLoader(pcS3URLs);

    if (this.destroyed || loadedPCChunks.length < 1) {
      pcS3URLs.map((path) => this.cache.clearCacheElement(path.part));
      return;
    }

    loadedPCChunks.map((chunk: any) => {
      const points =
        (chunk.data && PCLoader.simpleParse(chunk.data as Point3d[], 0xff0000)) ||
        new THREE.Object3D();
      this.cache.addPointcloud(chunk.id as string, points, undefined, chunk.bytes as number);
    });
  }

  isLoading() {
    return getWebWorkerManager().isLoading();
  }

  clearCustom() {
    this.cache.clearCache();

    getWebWorkerManager().stopChunks('pointcloudChunk');
    getWebWorkerManager().init('pointcloudChunk');
    this.destroyed = true;
    this.initialized = false;
  }

  /**
   *  Picks the point cloud and returns the intersection closest to the ray, if any, for rays that
   *  aren't from the camera. Rays from the camera pick with pickNearCursor.
   * @param raycaster
   */
  pick(raycaster: Raycaster): Intersection[] {
    const closest = closestToRay(this.intersect(raycaster));
    return closest ? [closest] : [];
  }

  intersect(raycaster: Raycaster): Intersection[] {
    if (!raycaster) {
      return [];
    }

    // The octree and its leafs are in the point cloud's own coordinates, see loadLodChunks
    const ownRaycaster = new Raycaster(
      raycaster.ray.origin.clone().sub(this.position),
      raycaster.ray.direction.clone(),
      raycaster.near,
      raycaster.far,
    );
    ownRaycaster.params = raycaster.params;

    const intersections: Intersection[] = [];
    for (const leaf of this.pickCachedBoundingTree(ownRaycaster)) {
      ownRaycaster.intersectObject(leaf.object, true, intersections);
    }

    return intersections
      .filter((intersection) => intersection.index !== undefined)
      .map((intersection) => {
        const position = (intersection.object as Points).geometry.attributes.position;
        const point = new THREE.Vector3(
          position.getX(intersection.index!),
          position.getY(intersection.index!),
          position.getZ(intersection.index!),
        ).add(this.position);
        return {
          ...intersection,
          point,
          distanceToRay: raycaster.ray.distanceToPoint(point),
        };
      });
  }

  /**
   *  Returns the picked bounding nodes that are cached,
   *  sorted with the shortest distance first
   * @param raycaster
   */
  private pickCachedBoundingTree(raycaster: Raycaster): IntersectedPointChunk[] {
    const pointCloudGeodata = ProjectGeodataManager.instance().getActivePointcloudGeodata();
    const octTree = pointCloudGeodata && pointCloudGeodata.octtree;
    if (!octTree) {
      return [];
    }

    const intersectedNodes = octTree.pickTree(raycaster);

    return intersectedNodes
      .reduce((prev: IntersectedPointChunk[], intersectedNode) => {
        if (this.cache.isCached(intersectedNode.node.data.pointcloud as string)) {
          prev.push({
            object: this.cache.get(intersectedNode.node.data.pointcloud as string),
            distance: intersectedNode.distance,
          });
        }
        return prev;
      }, [])
      .sort((a, b) => {
        return a.distance - b.distance;
      });
  }
}
